// src/pages/SingleFeedback.js - COMPLETELY RESPONSIVE VERSION
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Star, MessageCircle, Heart, Share, ExternalLink,
  ArrowLeft, Calendar, MapPin, User, ThumbsUp,
  Edit, Trash2, Send, Reply, ChevronRight,
  MoreVertical, Copy, Twitter, Facebook, Link as LinkIcon,
  ChevronDown, ChevronUp, Filter, Menu, X
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const SingleFeedback = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [replyText, setReplyText] = useState('');
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editReplyText, setEditReplyText] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [currentView, setCurrentView] = useState('feedback'); // 'feedback', 'replies', 'actions'
  const replyTextareaRef = useRef(null);

  useEffect(() => {
    if (id) {
      fetchFeedback();
    }
    
    // Focus reply textarea when component mounts on mobile
    const isMobile = window.innerWidth < 768;
    if (isMobile && replyTextareaRef.current) {
      setTimeout(() => {
        replyTextareaRef.current?.focus();
      }, 500);
    }
  }, [id]);

  const fetchFeedback = async () => {
    try {
      setLoading(true);
      setError('');
      
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      console.log('🔍 Fetching feedback ID:', id);
      
      const response = await fetch(`${API_URL}/api/feedback/${id}`, { headers });
      
      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Feedback not found');
        }
        throw new Error(`Failed to fetch: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.success || !data.feedback) {
        throw new Error(data.error || 'Invalid feedback data');
      }
      
      setFeedback(data.feedback);
    } catch (err) {
      console.error('❌ Error fetching feedback:', err.message);
      setError(err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async () => {
    if (!user) {
      alert('Please login to like feedback');
      navigate('/login', { state: { returnTo: `/feedback/${id}` } });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/feedback/${id}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFeedback(prev => ({
          ...prev,
          likes_count: data.likes_count,
          user_liked: data.liked
        }));
        
        // Show feedback on mobile
        if (window.innerWidth < 768) {
          alert(`You ${data.liked ? 'liked' : 'unliked'} this feedback!`);
        }
      }
    } catch (error) {
      console.error('Error liking feedback:', error);
      alert('Failed to like feedback');
    }
  };

  const handleShare = (platform = null) => {
    const shareUrl = window.location.href;
    const title = `Feedback from ${feedback.name}`;
    const text = feedback.message.substring(0, 100) + '...';
    
    if (platform === 'copy') {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
      setShowShareOptions(false);
      return;
    }
    
    
    // Default share (Web Share API or copy)
    if (navigator.share) {
      navigator.share({
        title,
        text,
        url: shareUrl,
      }).then(() => setShowShareOptions(false));
    } else {
      setShowShareOptions(true);
    }
  };

  const handleReplySubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      alert('Please login to reply');
      navigate('/login', { state: { returnTo: `/feedback/${id}` } });
      return;
    }

    if (!replyText.trim()) {
      alert('Please enter a reply');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/feedback/${id}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ message: replyText }),
      });

      if (response.ok) {
        const data = await response.json();
        setFeedback(prev => ({
          ...prev,
          replies: [...(prev.replies || []), data.reply]
        }));
        setReplyText('');
        setCurrentView('replies');
        
        // Scroll to bottom of replies on mobile
        if (window.innerWidth < 768) {
          setTimeout(() => {
            window.scrollTo({
              top: document.documentElement.scrollHeight,
              behavior: 'smooth'
            });
          }, 100);
        }
        
        alert('Reply posted successfully!');
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to post reply');
      }
    } catch (error) {
      console.error('Error posting reply:', error);
      alert('Failed to post reply');
    }
  };

  const handleEditReply = async (replyId) => {
    if (!editReplyText.trim()) {
      alert('Please enter a message');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/feedback/${id}/reply/${replyId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ message: editReplyText }),
      });

      if (response.ok) {
        const data = await response.json();
        setFeedback(prev => ({
          ...prev,
          replies: prev.replies.map(reply => 
            reply._id === replyId ? data.reply : reply
          )
        }));
        setEditingReplyId(null);
        setEditReplyText('');
        alert('Reply updated successfully!');
      }
    } catch (error) {
      console.error('Error editing reply:', error);
      alert('Failed to edit reply');
    }
  };

  const handleDeleteReply = async (replyId) => {
    if (!window.confirm('Are you sure you want to delete this reply?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/feedback/${id}/reply/${replyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        setFeedback(prev => ({
          ...prev,
          replies: prev.replies.filter(reply => reply._id !== replyId)
        }));
        alert('Reply deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting reply:', error);
      alert('Failed to delete reply');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: new Date().getFullYear() !== date.getFullYear() ? 'numeric' : undefined
      });
    }
  };

  const formatLongDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Mobile navigation tabs
  const MobileNavTabs = () => (
    <div className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 z-40 lg:hidden">
      <div className="flex justify-around items-center h-14">
        <button
          onClick={() => setCurrentView('feedback')}
          className={`flex flex-col items-center justify-center w-full h-full ${currentView === 'feedback' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <div className={`w-6 h-6 mb-1 ${currentView === 'feedback' ? 'text-blue-600' : ''}`}>
            <Star className="w-5 h-5 mx-auto" />
          </div>
          <span className="text-xs font-medium">Feedback</span>
        </button>
        
        <button
          onClick={() => setCurrentView('replies')}
          className={`flex flex-col items-center justify-center w-full h-full ${currentView === 'replies' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <div className="relative">
            <MessageCircle className="w-5 h-5 mx-auto mb-1" />
            {feedback?.replies?.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                {feedback.replies.length}
              </span>
            )}
          </div>
          <span className="text-xs font-medium">Replies</span>
        </button>
        
        <button
          onClick={() => setCurrentView('actions')}
          className={`flex flex-col items-center justify-center w-full h-full ${currentView === 'actions' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}
        >
          <div className="w-6 h-6 mb-1">
            <MoreVertical className="w-5 h-5 mx-auto" />
          </div>
          <span className="text-xs font-medium">More</span>
        </button>
      </div>
    </div>
  );

  // Share options dropdown
  const ShareOptions = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center md:items-center md:justify-center">
      <div 
        className="bg-white dark:bg-gray-800 w-full max-w-md rounded-t-2xl md:rounded-2xl shadow-2xl animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Share Feedback</h3>
          <button
            onClick={() => setShowShareOptions(false)}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-4">
          <div className="grid grid-cols-2 gap-3 mb-4">
            <button
              onClick={() => handleShare('copy')}
              className="flex flex-col items-center justify-center p-4 bg-blue-50 dark:bg-blue-900/30 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-800/30 transition"
            >
              <Copy className="w-6 h-6 text-blue-600 dark:text-blue-400 mb-2" />
              <span className="text-sm font-medium text-gray-900 dark:text-white">Copy Link</span>
            </button>
          </div>
          
          <div className="bg-gray-100 dark:bg-gray-700 rounded-lg p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-600 dark:text-gray-400">Share URL</span>
              <button
                onClick={() => navigator.clipboard.writeText(window.location.href)}
                className="text-xs text-blue-600 dark:text-blue-400 font-medium"
              >
                Copy
              </button>
            </div>
            <p className="text-sm text-gray-900 dark:text-white break-all">
              {window.location.href}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading feedback...</p>
        </div>
      </div>
    );
  }

  if (error || !feedback) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 flex items-center justify-center px-4">
        <div className="text-center max-w-sm w-full">
          <div className="text-7xl mb-4">😕</div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Feedback Not Found
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
            {error || 'The feedback you\'re looking for doesn\'t exist or has been removed.'}
          </p>
          <div className="flex flex-col gap-3">
            <Link
              to="/feedback"
              className="inline-flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-semibold text-sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to All Feedback
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-xl hover:bg-gray-300 dark:hover:bg-gray-600 transition font-semibold text-sm"
            >
              Go to Homepage
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Top Navigation - Desktop & Mobile */}
      <nav className="sticky top-0 z-30 bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            {/* Left: Back button */}
            <div className="flex items-center">
              <button
                onClick={() => navigate(-1)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 mr-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <Link
                to="/feedback"
                className="hidden lg:inline-flex items-center text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 text-sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                All Feedback
              </Link>
              <div className="hidden lg:block mx-4 text-gray-400">/</div>
              <div className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-[120px] sm:max-w-[200px] lg:max-w-none">
                Feedback from {feedback.name}
              </div>
            </div>
            
            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Desktop Share Button */}
              <button
                onClick={() => handleShare()}
                className="hidden sm:inline-flex items-center px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg hover:bg-green-200 dark:hover:bg-green-800/30 transition text-sm"
              >
                <Share className="w-4 h-4 mr-2" />
                Share
              </button>
              
              {/* Mobile Share Button */}
              <button
                onClick={() => handleShare()}
                className="sm:hidden p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
              >
                <Share className="w-5 h-5" />
              </button>
              
              {/* Like Button */}
              <button
                onClick={handleLike}
                className={`p-2 rounded-lg transition ${
                  feedback.user_liked
                    ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                }`}
              >
                <Heart className={`w-5 h-5 ${feedback.user_liked ? 'fill-red-500' : ''}`} />
              </button>
              
              {/* Mobile Menu */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <Menu className="w-5 h-5" />
              </button>
              
              {/* Desktop Home Button */}
              <Link
                to="/"
                className="hidden lg:inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-semibold"
              >
                Home
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Dropdown */}
      {isMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setIsMenuOpen(false)}>
          <div 
            className="absolute top-14 right-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-48 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-2">
              <Link
                to="/"
                className="flex items-center px-3 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link
                to="/feedback"
                className="flex items-center px-3 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                onClick={() => setIsMenuOpen(false)}
              >
                All Feedback
              </Link>
              <Link
                to="/contact"
                className="flex items-center px-3 py-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Main Content - Different views for mobile */}
        <div className="lg:hidden">
          {/* Mobile View */}
          {currentView === 'feedback' && (
            <div className="space-y-4 pb-20">
              {/* Rating Card */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 sm:w-5 sm:h-5 ${
                            i < feedback.rating
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {feedback.rating}.0/5.0
                    </span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDate(feedback.createdAt)}
                  </span>
                </div>
                
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                    {feedback.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-bold text-gray-900 dark:text-white text-base truncate">
                      {feedback.name}
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {feedback.email}
                    </p>
                    {feedback.city && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        <MapPin className="w-3 h-3 inline mr-1" />
                        {feedback.city}{feedback.state ? `, ${feedback.state}` : ''}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-3 mb-4">
                  <p className="text-gray-800 dark:text-gray-200 text-sm leading-relaxed">
                    "{feedback.message}"
                  </p>
                </div>
                
                {feedback.image && (
                  <img
                    src={feedback.image}
                    alt="Feedback"
                    className="w-full rounded-xl mb-4"
                    loading="lazy"
                  />
                )}
                
                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={handleLike}
                    className={`flex flex-col items-center justify-center p-3 rounded-xl transition ${
                      feedback.user_liked
                        ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    <Heart className={`w-5 h-5 mb-1 ${feedback.user_liked ? 'fill-red-500' : ''}`} />
                    <span className="text-xs font-semibold">{feedback.likes_count || 0}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Likes</span>
                  </button>
                  
                  <div className="flex flex-col items-center justify-center p-3 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl">
                    <MessageCircle className="w-5 h-5 mb-1" />
                    <span className="text-xs font-semibold">{feedback.replies?.length || 0}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Replies</span>
                  </div>
                  
                  <div className="flex flex-col items-center justify-center p-3 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-xl">
                    <Share className="w-5 h-5 mb-1" />
                    <span className="text-xs font-semibold">{feedback.shares_count || 0}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Shares</span>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">
                  Quick Actions
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setCurrentView('replies')}
                    className="flex items-center justify-center p-3 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-800/30 transition"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">View Replies</span>
                  </button>
                  
                  <button
                    onClick={() => handleShare()}
                    className="flex items-center justify-center p-3 bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-xl hover:bg-green-100 dark:hover:bg-green-800/30 transition"
                  >
                    <Share className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Share</span>
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {currentView === 'replies' && (
            <div className="space-y-4 pb-20">
              {/* Replies Header */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-blue-600" />
                    Replies ({feedback.replies?.length || 0})
                  </h2>
                  <button
                    onClick={() => setCurrentView('feedback')}
                    className="text-sm text-blue-600 dark:text-blue-400"
                  >
                    Back to Feedback
                  </button>
                </div>
                
                {/* Replies List */}
                {feedback.replies && feedback.replies.length > 0 ? (
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
                    {feedback.replies.map((reply) => (
                      <div 
                        key={reply._id} 
                        className="bg-gray-50 dark:bg-gray-700 rounded-xl p-3 border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-start gap-3 mb-2">
                          <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                            {reply.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start">
                              <h3 className="font-bold text-gray-900 dark:text-white text-sm truncate">
                                {reply.name}
                              </h3>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {formatDate(reply.createdAt)}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                              {reply.email}
                            </p>
                          </div>
                        </div>
                        
                        <p className="text-gray-700 dark:text-gray-300 text-sm">
                          {reply.message}
                        </p>
                        
                        {/* Reply Actions */}
                        {user && (user.id === reply.userId || user.role === 'admin') && (
                          <div className="flex gap-2 mt-3 pt-2 border-t border-gray-200 dark:border-gray-600">
                            {editingReplyId === reply._id ? (
                              <>
                                <button
                                  onClick={() => handleEditReply(reply._id)}
                                  className="flex-1 text-center text-xs text-green-600 font-medium py-1"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={() => {
                                    setEditingReplyId(null);
                                    setEditReplyText('');
                                  }}
                                  className="flex-1 text-center text-xs text-gray-600 py-1"
                                >
                                  Cancel
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => {
                                    setEditingReplyId(reply._id);
                                    setEditReplyText(reply.message);
                                  }}
                                  className="flex-1 text-center text-xs text-blue-600 py-1"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteReply(reply._id)}
                                  className="flex-1 text-center text-xs text-red-600 py-1"
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <MessageCircle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      No replies yet. Be the first to respond!
                    </p>
                  </div>
                )}
                
                {/* Add Reply Form */}
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">
                    Add Your Reply
                  </h3>
                  
                  {user ? (
                    <form onSubmit={handleReplySubmit}>
                      <textarea
                        ref={replyTextareaRef}
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Write your reply here..."
                        className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-700 dark:text-white resize-none"
                        rows="3"
                      />
                      <div className="flex justify-end mt-3">
                        <button
                          type="submit"
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition font-medium"
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Post Reply
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                        Please login to reply
                      </p>
                      <Link
                        to="/login"
                        state={{ returnTo: `/feedback/${id}` }}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition font-medium"
                      >
                        Login to Reply
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {currentView === 'actions' && (
            <div className="space-y-4 pb-20">
              {/* More Actions */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                  More Actions
                </h3>
                
                <div className="space-y-3">
                  <button
                    onClick={() => handleShare('copy')}
                    className="w-full flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition"
                  >
                    <div className="flex items-center">
                      <Copy className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-3" />
                      <span className="text-gray-900 dark:text-white text-sm font-medium">Copy Link</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </button>
                  
                  <Link
                    to="/feedback"
                    className="block w-full"
                  >
                    <div className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition">
                      <div className="flex items-center">
                        <MessageCircle className="w-5 h-5 text-blue-600 mr-3" />
                        <span className="text-gray-900 dark:text-white text-sm font-medium">All Feedback</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </Link>
                  
                  <Link
                    to="/"
                    className="block w-full"
                  >
                    <div className="flex items-center justify-between p-3 bg-gray-100 dark:bg-gray-700 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition">
                      <div className="flex items-center">
                        <ChevronRight className="w-5 h-5 text-purple-600 mr-3" />
                        <span className="text-gray-900 dark:text-white text-sm font-medium">Homepage</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </Link>
                </div>
              </div>
              
              {/* Feedback Info */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-4">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white mb-3">
                  Feedback Details
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Posted</span>
                    <span className="text-gray-900 dark:text-white">{formatLongDate(feedback.createdAt)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">ID</span>
                    <span className="text-gray-900 dark:text-white font-mono text-xs truncate ml-2">
                      {id.substring(0, 8)}...
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Status</span>
                    <span className="text-green-600 dark:text-green-400">Published</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Desktop View */}
        <div className="hidden lg:block space-y-6">
          {/* Main Feedback Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-start justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-5 h-5 ${
                            i < feedback.rating
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {feedback.rating}.0/5.0
                    </span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">•</span>
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      {formatLongDate(feedback.createdAt)}
                    </span>
                  </div>
                  
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                    Feedback from {feedback.name}
                  </h1>
                  
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {feedback.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{feedback.name}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{feedback.email}</p>
                      {feedback.city && (
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          <MapPin className="w-3 h-3 inline mr-1" />
                          {feedback.city}{feedback.state ? `, ${feedback.state}` : ''}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleLike}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                      feedback.user_liked
                        ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${feedback.user_liked ? 'fill-red-500' : ''}`} />
                    <span className="font-semibold">{feedback.likes_count || 0}</span>
                  </button>
                  
                  <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg">
                    <MessageCircle className="w-5 h-5" />
                    <span className="font-semibold">{feedback.replies?.length || 0}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-6 mb-6 border border-gray-200 dark:border-gray-600">
                <p className="text-gray-800 dark:text-gray-200 text-lg leading-relaxed whitespace-pre-wrap">
                  "{feedback.message}"
                </p>
              </div>
              
              {feedback.image && (
                <img
                  src={feedback.image}
                  alt="Feedback"
                  className="w-full max-w-2xl mx-auto rounded-xl shadow-lg mb-6"
                  loading="lazy"
                />
              )}
              
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                    {feedback.likes_count || 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Likes</div>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400 mb-1">
                    {feedback.replies?.length || 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Replies</div>
                </div>
                
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/30 dark:to-purple-800/30 rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mb-1">
                    {feedback.shares_count || 0}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">Shares</div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Replies Section */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-blue-600" />
              Replies ({feedback.replies?.length || 0})
            </h2>
            
            {feedback.replies && feedback.replies.length > 0 ? (
              <div className="space-y-4 mb-8 max-h-[400px] overflow-y-auto pr-2">
                {feedback.replies.map((reply) => (
                  <div 
                    key={reply._id} 
                    className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 border border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-600 transition-colors"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-teal-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                          {reply.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 dark:text-white">{reply.name}</h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {formatLongDate(reply.createdAt)}
                            {reply.updatedAt && reply.updatedAt !== reply.createdAt && (
                              <span className="ml-2 text-blue-500">(edited)</span>
                            )}
                          </p>
                        </div>
                      </div>
                      
                      {user && (user.id === reply.userId || user.role === 'admin') && (
                        <div className="flex items-center gap-2">
                          {editingReplyId === reply._id ? (
                            <>
                              <button
                                onClick={() => handleEditReply(reply._id)}
                                className="text-sm text-green-600 hover:text-green-800 font-semibold px-3 py-1"
                              >
                                Save
                              </button>
                              <button
                                onClick={() => {
                                  setEditingReplyId(null);
                                  setEditReplyText('');
                                }}
                                className="text-sm text-gray-600 hover:text-gray-800 px-3 py-1"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => {
                                  setEditingReplyId(reply._id);
                                  setEditReplyText(reply.message);
                                }}
                                className="text-sm text-blue-600 hover:text-blue-800 px-3 py-1"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteReply(reply._id)}
                                className="text-sm text-red-600 hover:text-red-800 px-3 py-1"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {editingReplyId === reply._id ? (
                      <textarea
                        value={editReplyText}
                        onChange={(e) => setEditReplyText(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white resize-none"
                        rows="3"
                      />
                    ) : (
                      <p className="text-gray-700 dark:text-gray-300">{reply.message}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <MessageCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 dark:text-gray-400">
                  No replies yet. Be the first to respond!
                </p>
              </div>
            )}
            
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                Add Your Reply
              </h3>
              
              {user ? (
                <form onSubmit={handleReplySubmit}>
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write your reply here..."
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-600 focus:outline-none dark:bg-gray-800 dark:text-white resize-none"
                    rows="4"
                  />
                  <div className="flex justify-end mt-4">
                    <button
                      type="submit"
                      className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                    >
                      <Send className="w-5 h-5 mr-2" />
                      Post Reply
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center py-6 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    Please login to reply to this feedback
                  </p>
                  <Link
                    to="/login"
                    state={{ returnTo: `/feedback/${id}` }}
                    className="inline-flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                  >
                    Login to Reply
                  </Link>
                </div>
              )}
            </div>
          </div>
          
          {/* Action Cards */}
          <div className="grid grid-cols-3 gap-6">
            <button
              onClick={handleLike}
              className={`flex flex-col items-center justify-center p-6 rounded-2xl transition ${
                feedback.user_liked
                  ? 'bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/30 dark:to-red-800/30 text-red-600 dark:text-red-400'
                  : 'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900/30 dark:to-gray-800/30 text-gray-700 dark:text-gray-300 hover:from-gray-100 hover:to-gray-200 dark:hover:from-gray-800/30 dark:hover:to-gray-700/30'
              }`}
            >
              <Heart className={`w-8 h-8 mb-3 ${feedback.user_liked ? 'fill-red-500' : ''}`} />
              <span className="font-semibold">Like</span>
              <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">{feedback.likes_count || 0} likes</span>
            </button>
            
            <button
              onClick={() => handleShare()}
              className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 text-green-700 dark:text-green-400 rounded-2xl hover:from-green-100 hover:to-green-200 dark:hover:from-green-800/30 dark:hover:to-green-700/30 transition"
            >
              <Share className="w-8 h-8 mb-3" />
              <span className="font-semibold">Share</span>
              <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">Share this feedback</span>
            </button>
            
            <Link
              to="/feedback"
              className="flex flex-col items-center justify-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-800/30 text-blue-700 dark:text-blue-400 rounded-2xl hover:from-blue-100 hover:to-blue-200 dark:hover:from-blue-800/30 dark:hover:to-blue-700/30 transition"
            >
              <Reply className="w-8 h-8 mb-3" />
              <span className="font-semibold">More</span>
              <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">View all feedback</span>
            </Link>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Navigation Tabs */}
      <MobileNavTabs />
      
      {/* Share Options Modal */}
      {showShareOptions && <ShareOptions />}

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        @keyframes scale-in {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        
        .animate-scale-in {
          animation: scale-in 0.2s ease-out;
        }
        
        /* Custom scrollbar */
        .overflow-y-auto::-webkit-scrollbar {
          width: 4px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        
        .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        
        .dark .overflow-y-auto::-webkit-scrollbar-track {
          background: #374151;
        }
        
        .dark .overflow-y-auto::-webkit-scrollbar-thumb {
          background: #6b7280;
        }
        
        .dark .overflow-y-auto::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default SingleFeedback;